import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InternationalPhoneField } from '@/components/phone/InternationalPhoneField';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  deriveInternationalMobileDraft,
  resolveInternationalMobile,
} from '@/lib/phone/resolveInternationalMobile';
import { OnboardingVideoPlayer } from '@/components/onboarding/OnboardingVideoPlayer';
import type { ViralizeQualificationSnapshot } from '@/lib/viralize-qualification/buildItinerary';
import { VIRALIZE_QUALIFICATION_INTRO_VIDEO_SRC } from '@/lib/viralize-qualification/introVideo';
import { remainingUnlockSeconds } from '@/lib/viralize-qualification/remainingUnlockSeconds';
import {
  VIRALIZE_QUESTIONNAIRE,
  type ViralizeQualificationAnswerInput,
  type ViralizeQualificationField,
  type ViralizeQualificationOptionForField,
  type ViralizeQualificationStage,
  viralizeQualificationOptionKey,
  viralizeQualificationQuestionKey,
} from '@/lib/viralize-qualification/questionnaire';
import { cn } from '@/lib/utils';

import { qualificationOptionIcon } from './qualificationOptionIcons';

export interface ViralizeQualificationContactInput {
  name?: string;
  phoneDetails?: { e164: string; country: string };
}

export interface ViralizeQualificationModalProps {
  open: boolean;
  itinerary: readonly ViralizeQualificationStage[];
  profile: { name?: string | null; phone_number?: string | null };
  qualification: ViralizeQualificationSnapshot | null;
  onSaveContact: (input: ViralizeQualificationContactInput) => Promise<void>;
  onSaveAnswer: (
    input: ViralizeQualificationAnswerInput,
  ) => Promise<ViralizeQualificationSnapshot>;
  onComplete: () => void;
  onClose: () => void;
}

type DraftValue = string | boolean | string[] | undefined;

interface ContactDraft {
  name: string;
  country: string;
  phone: string;
}

const questionByField = new Map(
  VIRALIZE_QUESTIONNAIRE.map((question) => [question.field, question]),
);

function ownValue(
  snapshot: ViralizeQualificationSnapshot | null,
  field: ViralizeQualificationField,
): unknown {
  if (!snapshot || !Object.prototype.hasOwnProperty.call(snapshot, field)) {
    return undefined;
  }
  return snapshot[field];
}

function seedAnswerDraft(
  stage: ViralizeQualificationStage | undefined,
  snapshot: ViralizeQualificationSnapshot | null,
): DraftValue {
  if (!stage || stage === 'contact' || stage === 'intro_video') return undefined;
  const question = questionByField.get(stage);
  if (!question) return undefined;
  const value = ownValue(snapshot, stage);

  if (question.kind === 'multiple') {
    if (!Array.isArray(value)) return [];
    return question.values.filter((option) => value.includes(option));
  }
  return question.values.find((option) => option === value);
}

function answerInputForStage(
  stage: Exclude<ViralizeQualificationStage, 'contact' | 'intro_video'>,
  value: DraftValue,
): ViralizeQualificationAnswerInput {
  switch (stage) {
    case 'motivation':
      return {
        motivation: value as ViralizeQualificationOptionForField<'motivation'>,
      };
    case 'starting_difficulty':
      return {
        startingDifficulty:
          value as ViralizeQualificationOptionForField<'starting_difficulty'>,
      };
    case 'equipment':
      return {
        equipment: value as ViralizeQualificationOptionForField<'equipment'>[],
      };
    case 'monthly_income':
      return {
        monthlyIncome:
          value as ViralizeQualificationOptionForField<'monthly_income'>,
      };
    case 'clipper_experience':
      return {
        clipperExperience:
          value as ViralizeQualificationOptionForField<'clipper_experience'>,
      };
    case 'could_earn_more':
      return {
        couldEarnMore:
          value as ViralizeQualificationOptionForField<'could_earn_more'>,
      };
    case 'financially_stuck':
      return {
        financiallyStuck:
          value as ViralizeQualificationOptionForField<'financially_stuck'>,
      };
    case 'likes_current_job':
      return {
        likesCurrentJob:
          value as ViralizeQualificationOptionForField<'likes_current_job'>,
      };
    case 'wants_remote_mobile_work':
      return {
        wantsRemoteMobileWork:
          value as ViralizeQualificationOptionForField<'wants_remote_mobile_work'>,
      };
    case 'self_employment_outlook':
      return {
        selfEmploymentOutlook:
          value as ViralizeQualificationOptionForField<'self_employment_outlook'>,
      };
    case 'first_30_day_revenue_outlook':
      return {
        first30DayRevenueOutlook:
          value as ViralizeQualificationOptionForField<'first_30_day_revenue_outlook'>,
      };
  }
}

function displayValue(value: string | boolean): string {
  return typeof value === 'boolean' ? String(value) : value;
}

function parsedDisplayValue(
  field: ViralizeQualificationField,
  value: string,
): string | boolean | undefined {
  const question = questionByField.get(field);
  return question?.values.find((option) => displayValue(option) === value);
}

export function ViralizeQualificationModal({
  open,
  itinerary,
  profile,
  qualification,
  onSaveContact,
  onSaveAnswer,
  onComplete,
  onClose,
}: ViralizeQualificationModalProps) {
  const { t } = useTranslation();
  const initialPhone = deriveInternationalMobileDraft(profile.phone_number);
  const [sessionItinerary, setSessionItinerary] = useState<
    readonly ViralizeQualificationStage[]
  >(() => [...itinerary]);
  const [sessionProfile, setSessionProfile] = useState(() => ({ ...profile }));
  const [stageIndex, setStageIndex] = useState(0);
  const [canonical, setCanonical] =
    useState<ViralizeQualificationSnapshot | null>(qualification);
  const [answerDraft, setAnswerDraft] = useState<DraftValue>(() =>
    seedAnswerDraft(itinerary[0], qualification),
  );
  const [contactDraft, setContactDraft] = useState<ContactDraft>({
    name: typeof profile.name === 'string' ? profile.name : '',
    country: initialPhone.country,
    phone: initialPhone.phone,
  });
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [error, setError] = useState<string | null>(null);
  const introUnlockStartedAtRef = useRef<number | null>(null);
  const mountedRef = useRef(false);
  const generationRef = useRef(0);
  const flightRef = useRef<symbol | null>(null);
  const completedGenerationRef = useRef<number | null>(null);
  const previousOpenRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const titleRef = useRef<HTMLHeadingElement>(null);
  onCompleteRef.current = onComplete;
  const opening = open && !previousOpenRef.current;
  const activeItinerary = opening ? itinerary : sessionItinerary;
  const activeProfile = opening ? profile : sessionProfile;
  const activeStageIndex = opening ? 0 : stageIndex;
  const stage = activeItinerary[activeStageIndex];
  const question =
    stage && stage !== 'contact' && stage !== 'intro_video'
      ? questionByField.get(stage)
      : undefined;
  const visibleAnswerDraft = opening
    ? seedAnswerDraft(stage, qualification)
    : answerDraft;
  const openingPhone = deriveInternationalMobileDraft(profile.phone_number);
  const visibleContactDraft = opening
    ? {
        name: typeof profile.name === 'string' ? profile.name : '',
        country: openingPhone.country,
        phone: openingPhone.phone,
      }
    : contactDraft;

  const resetSession = useCallback(() => {
    const phone = deriveInternationalMobileDraft(profile.phone_number);
    const nextItinerary = [...itinerary];
    generationRef.current += 1;
    flightRef.current = null;
    completedGenerationRef.current = null;
    setSessionItinerary(nextItinerary);
    setSessionProfile({ ...profile });
    setCanonical(qualification);
    setStageIndex(0);
    setAnswerDraft(seedAnswerDraft(nextItinerary[0], qualification));
    setContactDraft({
      name: typeof profile.name === 'string' ? profile.name : '',
      country: phone.country,
      phone: phone.phone,
    });
    setSubmitting(false);
    setCompleted(false);
    setNowMs(Date.now());
    introUnlockStartedAtRef.current = null;
    setError(null);
  }, [itinerary, profile, qualification]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      flightRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = open;
    if (!open) {
      if (wasOpen) {
        generationRef.current += 1;
        flightRef.current = null;
        setSubmitting(false);
      }
      return;
    }

    if (!wasOpen) {
      resetSession();
    }
  }, [open, resetSession]);

  useLayoutEffect(() => {
    if (open && stage) titleRef.current?.focus();
  }, [open, stage, stageIndex]);

  useEffect(() => {
    if (!open || stage !== 'intro_video') return;
    if (introUnlockStartedAtRef.current === null) {
      introUnlockStartedAtRef.current = Date.now();
    }
    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 250);
    return () => window.clearInterval(intervalId);
  }, [open, stage]);

  const introUnlockRemaining =
    stage === 'intro_video' && introUnlockStartedAtRef.current !== null
      ? remainingUnlockSeconds({
          startedAtMs: introUnlockStartedAtRef.current,
          nowMs,
        })
      : 0;

  const missingName =
    typeof activeProfile.name !== 'string' || !activeProfile.name.trim();
  const profilePhone = deriveInternationalMobileDraft(activeProfile.phone_number);
  const missingPhone = !profilePhone.valid;
  const resolvedPhone = resolveInternationalMobile({
    country: visibleContactDraft.country,
    phone: visibleContactDraft.phone,
  });
  const contactValid =
    (!missingName || visibleContactDraft.name.trim().length > 0) &&
    (!missingPhone || resolvedPhone !== null);
  const answerValid =
    question?.kind === 'multiple'
      ? Array.isArray(visibleAnswerDraft)
      : visibleAnswerDraft !== undefined;

  const finishOrAdvance = useCallback(
    (snapshot: ViralizeQualificationSnapshot | null) => {
      const nextIndex = stageIndex + 1;
      if (nextIndex >= sessionItinerary.length) {
        if (completedGenerationRef.current !== generationRef.current) {
          completedGenerationRef.current = generationRef.current;
          setCompleted(true);
          onCompleteRef.current();
        }
        return;
      }
      setStageIndex(nextIndex);
      setAnswerDraft(seedAnswerDraft(sessionItinerary[nextIndex], snapshot));
      setError(null);
    },
    [sessionItinerary, stageIndex],
  );

  const saveCurrentStage = async () => {
    if (
      !stage ||
      flightRef.current ||
      submitting ||
      completedGenerationRef.current === generationRef.current
    ) {
      return;
    }
    if (stage === 'intro_video') {
      finishOrAdvance(canonical);
      return;
    }
    if (stage === 'contact' ? !contactValid : !answerValid) return;

    const generation = generationRef.current;
    const flight = Symbol('qualification-save');
    flightRef.current = flight;
    setSubmitting(true);
    setError(null);

    try {
      if (stage === 'contact') {
        const input: ViralizeQualificationContactInput = {};
        if (missingName) input.name = visibleContactDraft.name.trim();
        if (missingPhone && resolvedPhone) {
          input.phoneDetails = resolvedPhone;
        }
        await onSaveContact(input);
        if (
          !mountedRef.current ||
          generationRef.current !== generation ||
          flightRef.current !== flight
        ) {
          return;
        }
        finishOrAdvance(canonical);
      } else {
        const nextSnapshot = await onSaveAnswer(
          answerInputForStage(stage, visibleAnswerDraft),
        );
        if (
          !mountedRef.current ||
          generationRef.current !== generation ||
          flightRef.current !== flight
        ) {
          return;
        }
        setCanonical(nextSnapshot);
        finishOrAdvance(nextSnapshot);
      }
    } catch {
      if (
        mountedRef.current &&
        generationRef.current === generation &&
        flightRef.current === flight
      ) {
        setError(t('viralizeQualification.saveError'));
      }
    } finally {
      if (
        mountedRef.current &&
        generationRef.current === generation &&
        flightRef.current === flight
      ) {
        flightRef.current = null;
        setSubmitting(false);
      }
    }
  };

  const goBack = () => {
    if (stageIndex <= 0 || submitting) return;
    const previousIndex = stageIndex - 1;
    setStageIndex(previousIndex);
    setAnswerDraft(seedAnswerDraft(sessionItinerary[previousIndex], canonical));
    setError(null);
  };

  const selectedEquipment = useMemo(
    () => (Array.isArray(visibleAnswerDraft) ? visibleAnswerDraft : []),
    [visibleAnswerDraft],
  );

  const toggleEquipment = (value: string, checked: boolean) => {
    const questionValues = VIRALIZE_QUESTIONNAIRE.find(
      (item) => item.field === 'equipment',
    ).values;
    const selected = new Set(selectedEquipment);
    if (checked) selected.add(value);
    else selected.delete(value);
    setAnswerDraft(questionValues.filter((option) => selected.has(option)));
  };

  if (!stage) return null;

  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent
        data-sentry-block=""
        data-viralize-qualification-private=""
        hideCloseButton
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          titleRef.current?.focus();
        }}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
        className="rr-block max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-2xl gap-0 overflow-x-hidden overflow-y-auto rounded-2xl border-primary/25 bg-card p-0 text-foreground shadow-lg sm:max-h-[calc(100dvh-3rem)] sm:w-[calc(100%-3rem)]"
      >
        <div className="border-b border-border bg-background/45 px-4 py-4 sm:px-7 sm:py-5">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
            {t('viralizeQualification.title')}
          </p>
          <DialogDescription
            className={cn(
              'mt-1 max-w-[58ch] text-left leading-relaxed text-muted-foreground',
              stage === 'intro_video' && 'sr-only',
            )}
          >
            {stage === 'intro_video'
              ? t('viralizeQualification.introVideo.iframeTitle')
              : t('viralizeQualification.description')}
          </DialogDescription>
        </div>

        <div className="grid min-w-0 gap-6 px-4 py-5 sm:px-7 sm:py-7">
          {activeItinerary.length > 1 && (
          <div className="flex min-w-0 items-center gap-3" aria-hidden="true">
            <span className="shrink-0 text-xs font-medium text-muted-foreground">
              {t('viralizeQualification.progress', {
                current: activeStageIndex + 1,
                total: activeItinerary.length,
              })}
            </span>
            <div className="flex min-w-0 flex-1 gap-1.5">
              {activeItinerary.map((item, index) => (
                <span
                  key={`${item}-${index}`}
                  className={cn(
                    'h-1.5 min-w-1 flex-1 rounded-full border border-border bg-secondary',
                    index <= activeStageIndex && 'border-primary bg-primary',
                  )}
                />
              ))}
            </div>
          </div>
          )}

          <section className="min-w-0" aria-busy={submitting}>
            <DialogTitle
              ref={titleRef}
              tabIndex={-1}
              className="max-w-[26ch] text-left text-2xl font-semibold leading-tight tracking-tight focus:outline-none sm:text-3xl"
            >
              {stage === 'contact'
                ? t('viralizeQualification.contact.title')
                : stage === 'intro_video'
                  ? t('viralizeQualification.introVideo.title')
                  : t(viralizeQualificationQuestionKey(stage))}
            </DialogTitle>

            {stage === 'intro_video' ? (
              <div
                data-testid="viralize-qualification-intro-video"
                className="mt-6"
              >
                <OnboardingVideoPlayer
                  src={VIRALIZE_QUALIFICATION_INTRO_VIDEO_SRC}
                  label={t('viralizeQualification.introVideo.iframeTitle')}
                  frameClassName="group relative mx-auto aspect-video w-full overflow-hidden rounded-xl border border-primary/20 bg-background shadow-[0_24px_80px_-28px_hsl(var(--primary)/0.38)] outline-none ring-offset-background transition-shadow motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  playLabel={t('onboarding.courseOffer.player.play')}
                  pauseLabel={t('onboarding.courseOffer.player.pause')}
                  seekLabel={t('onboarding.courseOffer.player.seek')}
                  muteLabel={t('onboarding.courseOffer.player.mute')}
                  unmuteLabel={t('onboarding.courseOffer.player.unmute')}
                  volumeLabel={t('onboarding.courseOffer.player.volume')}
                  fullscreenLabel={t('onboarding.courseOffer.player.fullscreen')}
                  enableAudioLabel={t('onboarding.courseOffer.player.enableAudio')}
                  errorTitle={t('onboarding.courseOffer.player.errorTitle')}
                  retryLabel={t('onboarding.courseOffer.player.retry')}
                />
              </div>
            ) : stage === 'contact' ? (
              <div className="mt-6 grid gap-5">
                {missingName && (
                  <div className="grid gap-2">
                    <Label htmlFor="viralize-qualification-name" className="text-sm font-medium">
                      {t('viralizeQualification.contact.name')}
                    </Label>
                    <Input
                      id="viralize-qualification-name"
                      value={visibleContactDraft.name}
                      onChange={(event) =>
                        setContactDraft((current) => ({
                          ...current,
                          name: event.target.value,
                        }))
                      }
                      disabled={submitting}
                      autoComplete="name"
                      aria-invalid={!visibleContactDraft.name.trim()}
                      aria-required="true"
                      required
                      className="h-11 bg-background"
                    />
                  </div>
                )}
                {missingPhone && (
                  <InternationalPhoneField
                    id="viralize-qualification-phone"
                    label={t('viralizeQualification.contact.whatsapp')}
                    countryLabel={t('viralizeQualification.contact.country')}
                    searchLabel={t('viralizeQualification.contact.search')}
                    notFoundLabel={t('viralizeQualification.contact.notFound')}
                    placeholder={t('viralizeQualification.contact.placeholder')}
                    country={visibleContactDraft.country}
                    phone={visibleContactDraft.phone}
                    onCountryChange={(country) =>
                      setContactDraft((current) => ({ ...current, country }))
                    }
                    onPhoneChange={(phone) =>
                      setContactDraft((current) => ({ ...current, phone }))
                    }
                    disabled={submitting}
                    invalid={!resolvedPhone}
                    required
                    privateReplayBlock
                  />
                )}
              </div>
            ) : question?.kind === 'multiple' ? (
              <div className="mt-6 grid gap-3" role="group" aria-label={t(viralizeQualificationQuestionKey(stage))}>
                {question.values.map((value) => {
                  const selected = selectedEquipment.includes(value);
                  const label = t(viralizeQualificationOptionKey('equipment', value));
                  const OptionIcon = qualificationOptionIcon('equipment', value);
                  return (
                    <Label
                      key={value}
                      data-selected={selected}
                      className="flex min-h-14 cursor-pointer items-center gap-3 rounded-xl border border-border bg-background px-3 py-2.5 text-sm font-medium transition-colors hover:border-primary/55 hover:bg-secondary/60 data-[selected=true]:border-primary data-[selected=true]:bg-primary/10 sm:px-4"
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={(checked) =>
                          toggleEquipment(value, checked === true)
                        }
                        disabled={submitting}
                        className="h-11 w-11 rounded-lg"
                        aria-label={label}
                      />
                      <OptionIcon
                        aria-hidden="true"
                        data-qualification-option-icon={`equipment:${String(value)}`}
                        className={cn(
                          'size-[18px] shrink-0 transition-colors',
                          selected ? 'text-primary' : 'text-muted-foreground',
                        )}
                        strokeWidth={2}
                      />
                      <span className="min-w-0 flex-1 leading-snug">{label}</span>
                    </Label>
                  );
                })}
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t('viralizeQualification.equipmentOptionalHint')}
                </p>
              </div>
            ) : question ? (
              <RadioGroup
                key={stage}
                className="mt-6 grid gap-3"
                aria-label={t(viralizeQualificationQuestionKey(stage))}
                required
                value={
                  visibleAnswerDraft === undefined
                    ? undefined
                    : displayValue(visibleAnswerDraft as string | boolean)
                }
                onValueChange={(value) =>
                  setAnswerDraft(parsedDisplayValue(stage, value))
                }
                disabled={submitting}
              >
                {question.values.map((value) => {
                  const stringValue = displayValue(value);
                  const selected = visibleAnswerDraft === value;
                  const label = t(viralizeQualificationOptionKey(stage, value));
                  const OptionIcon = qualificationOptionIcon(stage, value);
                  return (
                    <Label
                      key={stringValue}
                      data-selected={selected}
                      className="flex min-h-14 cursor-pointer items-center gap-3 rounded-xl border border-border bg-background px-3 py-2.5 text-sm font-medium transition-colors hover:border-primary/55 hover:bg-secondary/60 data-[selected=true]:border-primary data-[selected=true]:bg-primary/10 sm:px-4"
                    >
                      <RadioGroupItem
                        value={stringValue}
                        className="h-11 w-11 border-border data-[state=checked]:border-primary data-[state=checked]:bg-primary/10"
                        aria-label={label}
                      />
                      <OptionIcon
                        aria-hidden="true"
                        data-qualification-option-icon={`${stage}:${String(value)}`}
                        className={cn(
                          'size-[18px] shrink-0 transition-colors',
                          selected ? 'text-primary' : 'text-muted-foreground',
                        )}
                        strokeWidth={2}
                      />
                      <span className="min-w-0 flex-1 leading-snug">{label}</span>
                    </Label>
                  );
                })}
              </RadioGroup>
            ) : null}
          </section>

          <div
            aria-live="polite"
            className="min-h-5 text-sm font-medium text-destructive"
          >
            {error}
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={stage === 'intro_video' ? onClose : goBack}
              disabled={
                stage === 'intro_video'
                  ? false
                  : activeStageIndex === 0 || submitting
              }
              className="min-h-11 px-4 text-muted-foreground sm:min-w-28"
            >
              {stage !== 'intro_video' && <ChevronLeft aria-hidden="true" />}
              {t(
                stage === 'intro_video'
                  ? 'viralizeQualification.exit'
                  : 'viralizeQualification.back',
              )}
            </Button>
            <Button
              type="button"
              onClick={saveCurrentStage}
              disabled={
                submitting ||
                completed ||
                (stage === 'intro_video'
                  ? introUnlockRemaining > 0
                  : stage === 'contact'
                    ? !contactValid
                    : !answerValid)
              }
              className="min-h-11 px-6 font-semibold active:scale-[0.98] sm:min-w-36"
            >
              {submitting
                ? t('viralizeQualification.saving')
                : stage === 'intro_video' && introUnlockRemaining > 0
                  ? t('viralizeQualification.introVideo.countdown', {
                      seconds: introUnlockRemaining,
                    })
                  : t('viralizeQualification.continue')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
